import { createContext, useContext, useEffect, useState } from "react";
import { Link, NavLink, Navigate, Outlet, RouterProvider, createBrowserRouter, isRouteErrorResponse, useLocation, useNavigate, useRouteError } from "react-router-dom";
import { canOpenConfig, isConfigPath, receptionPathAllowed } from "../shared/access";
import { api, ApiError } from "./api";
import { AvailabilityPage } from "./pages/availability";
import { CataloguePage } from "./pages/catalogue";
import { ClientProposalPage, InquirePage } from "./pages/public";
import { ContractSignPage } from "./pages/contract-sign";
import { GuestListPage } from "./pages/guest-list";
import { MenuChoicePage } from "./pages/menu-choice";
import { EntryPage } from "./pages/entry";
import { TastingPage } from "./pages/tasting";
import { EventPage } from "./pages/event";
import { ImportPage } from "./pages/import";
import { InboxPage } from "./pages/inbox";
import { LeadStepPage } from "./pages/lead-step";
import { LoginPage } from "./pages/login";
import { OverviewPage } from "./pages/overview";
import { FollowUpPage } from "./pages/followup";
import { useFollowUps } from "./followups";
import { FollowUpPopup } from "./followup-popup";
import { PipelinePage } from "./pages/pipeline";
import { ProcessPage } from "./pages/process";
import { ProposalPage } from "./pages/proposal";
import { RatesPage } from "./pages/rates";
import { SettingsPage } from "./pages/settings";
import { VariablesPage } from "./pages/variables";
import { VenuePage, VenuesPage } from "./pages/venues";
import type { Role } from "../shared/permissions";

export type Staff = {
  id: string;
  email: string;
  display_name: string;
  role: Role;
  permissions: string[];
};

export type Session = {
  user: Staff;
  business: { name: string; currency: string; timezone: string; language: string };
  integrations: Record<string, { status: string; detail: string }>;
};

const SessionContext = createContext<Session | null>(null);

export function useSession(): Session {
  const value = useContext(SessionContext);
  if (!value) throw new Error("Συνδέσου πριν ανοίξεις το στούντιο.");
  return value;
}

const links = [
  ["/", "Επισκόπηση"],
  ["/followup", "Follow-up"],
  ["/inbox", "Εισερχόμενα"],
  ["/pipeline", "Εκδηλώσεις"],
  ["/availability", "Διαθεσιμότητα"],
  ["/tasting", "Γευσιγνωσία"],
  ["/venues", "Χώροι"],
  ["/rates", "Τιμές"],
  ["/variables", "Μεταβλητές"],
  ["/process", "Ροή"],
  ["/imports", "Εισαγωγή"],
  ["/catalogue", "Κατάλογος"],
  ["/settings", "Ρυθμίσεις"],
] as const;

const ROLE_LABELS: Record<string, string> = {
  superadmin: "Superadmin",
  owner: "Χειριστής",
  sales: "Χειριστής",
  operations: "Χρήστης",
  viewer: "Χρήστης",
  reception: "Υποδοχή",
};

const RECEPTION_NAV = new Set(["/inbox", "/tasting"]);

function Shell() {
  const [session, setSession] = useState<Session | null>(null);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const followUps = useFollowUps(Boolean(session) && session?.user.role !== "reception");

  useEffect(() => {
    api<Session>("/api/auth/me")
      .then(setSession)
      .catch((reason: unknown) => {
        if (!(reason instanceof ApiError) || reason.status !== 401) setError(reason instanceof Error ? reason.message : "Το στούντιο δεν άνοιξε.");
      })
      .finally(() => setReady(true));
  }, []);

  if (!ready) return <p className="main">Ανοιγμα του στούντιο…</p>;
  if (!session) return <Navigate to="/login" replace />;
  const reception = session.user.role === "reception";
  const config = canOpenConfig(session.user);
  const navLinks = reception ? links.filter(([path]) => RECEPTION_NAV.has(path)) : links.filter(([path]) => config || !isConfigPath(path));
  if (reception && !receptionPathAllowed(location.pathname)) return <Navigate to="/inbox" replace />;

  return (
    <SessionContext.Provider value={session}>
      <div className="shell">
        <aside className="nav">
          <div className="nav-bar">
            <Link className="brand" to={reception ? "/inbox" : "/"} aria-label="Αρχική" onClick={() => setMenuOpen(false)}>
              <strong>{session.business.name}</strong>
              <span>{session.business.timezone} · {session.business.currency}</span>
            </Link>
            <button className="secondary menu-toggle" type="button" aria-expanded={menuOpen} onClick={() => setMenuOpen((open) => !open)}>
              {menuOpen ? "Κλείσιμο" : "Μενού"}
              {!menuOpen && followUps.list && followUps.list.count > 0 ? <span className="nav-badge hot menu-badge" aria-label={`${followUps.list.count} follow-up σήμερα`}>{followUps.list.count}</span> : null}
            </button>
          </div>
          <div className={menuOpen ? "nav-panel open" : "nav-panel"}>
            <nav>
              {navLinks.map(([path, label]) => (
                <NavLink key={path} className={path === "/followup" ? "nav-followup" : undefined} to={path} end={path === "/"} onClick={() => setMenuOpen(false)}>
                  {label}
                  {path === "/followup" && followUps.list ? (
                    <span className={followUps.list.count > 0 ? "nav-badge hot" : "nav-badge"} aria-label={`${followUps.list.count} follow-up σήμερα`}>{followUps.list.count}</span>
                  ) : null}
                </NavLink>
              ))}
            </nav>
            <div className="nav-actions">
              <NavLink className="nav-new" to="/lead/new" onClick={() => setMenuOpen(false)}>
                + Νέα εκδήλωση
              </NavLink>
              <Link className="nav-new secondary" to="/tasting" onClick={() => setMenuOpen(false)}>
                + Νέα γευσιγνωσία
              </Link>
            </div>
            <div className="who">
              <span>{session.user.display_name} · {ROLE_LABELS[session.user.role] ?? session.user.role}</span>
              <button
                className="logout-link"
                type="button"
                onClick={() => {
                  void api("/api/auth/logout", { method: "POST" }).finally(() => navigate("/login"));
                }}
              >
                <svg aria-hidden="true" viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                  <path d="M16 17l5-5-5-5" />
                  <path d="M21 12H9" />
                </svg>
                <span>Εξοδος</span>
              </button>
            </div>
          </div>
        </aside>
        <main className="main">
          {!reception && followUps.list ? <FollowUpPopup userId={session.user.id} list={followUps.list} /> : null}
          {error ? <p className="banner error">{error}</p> : !config && isConfigPath(location.pathname) ? <ConfigOnly /> : <Outlet />}
        </main>
      </div>
    </SessionContext.Provider>
  );
}

function ConfigOnly() {
  return (
    <section className="panel">
      <h1>Μόνο για superadmin</h1>
      <p className="lede">Οι Χώροι, οι Τιμές, οι Μεταβλητές, η Ροή, η Εισαγωγή, ο Κατάλογος και οι Ρυθμίσεις ανοίγουν μόνο με λογαριασμό superadmin.</p>
      <div className="actions">
        <NavLink className="button" to="/">Επισκόπηση</NavLink>
      </div>
    </section>
  );
}

function MissingPage() {
  return (
    <div className="login">
      <section className="panel">
        <h1>1234 MASTER</h1>
        <p className="lede">Αυτή η διεύθυνση δεν είναι σελίδα του στούντιο.</p>
        <div className="actions">
          <a className="button" href="/">Ανοιγμα</a>
          <a className="button secondary" href="/login">Σύνδεση</a>
        </div>
      </section>
    </div>
  );
}

function RouteError() {
  const error = useRouteError();
  if (isRouteErrorResponse(error) && error.status === 404) return <MissingPage />;
  const message = isRouteErrorResponse(error) ? error.statusText || "Η σελίδα δεν άνοιξε." : error instanceof Error ? error.message : "Η σελίδα δεν άνοιξε.";
  return (
    <div className="login">
      <section className="panel">
        <h1>1234 MASTER</h1>
        <p className="lede">{message}</p>
        <a className="button" href="/">Ανοιγμα</a>
      </section>
    </div>
  );
}

const router = createBrowserRouter([
  {
    element: <Outlet />,
    errorElement: <RouteError />,
    children: [
  { path: "/login", element: <LoginPage /> },
  { path: "/inquire", element: <InquirePage /> },
  { path: "/p/:token", element: <ClientProposalPage /> },
  { path: "/menu-choice/:token", element: <MenuChoicePage /> },
  { path: "/contract/:token", element: <ContractSignPage /> },
  { path: "/guests/:token", element: <GuestListPage /> },
  {
    path: "/",
    element: <Shell />,
    children: [
      { index: true, element: <OverviewPage /> },
      { path: "venues", element: <VenuesPage /> },
      { path: "venues/:id", element: <VenuePage /> },
      { path: "rates", element: <RatesPage /> },
      { path: "availability", element: <AvailabilityPage /> },
      { path: "variables", element: <VariablesPage /> },
      { path: "pipeline", element: <PipelinePage /> },
      { path: "process", element: <ProcessPage /> },
      { path: "followup", element: <FollowUpPage /> },
      { path: "inbox", element: <InboxPage /> },
      { path: "inbox/:intakeId", element: <LeadStepPage /> },
      { path: "lead/new", element: <LeadStepPage /> },
      { path: "lead/:visitId", element: <LeadStepPage /> },
      { path: "imports", element: <ImportPage /> },
      { path: "catalogue", element: <CataloguePage /> },
      { path: "settings", element: <SettingsPage /> },
      { path: "tasting/:visitId?", element: <TastingPage /> },
      { path: "events/:eventId/tasting", element: <TastingPage /> },
      { path: "events/new", element: <Navigate to="/lead/new" replace /> },
      { path: "events/:eventId/card", element: <LeadStepPage /> },
      { path: "events/:id/edit", element: <EntryPage /> },
      { path: "events/:id", element: <EventPage /> },
      { path: "proposals/:id", element: <ProposalPage /> },
    ],
  },
  { path: "*", element: <MissingPage /> },
    ],
  },
]);

export function App() {
  return <RouterProvider router={router} />;
}
